import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash.debounce';
import styled from 'styled-components';
import Form from 'antd/es/form';
import axios from 'axios';
import { Select, Typography } from 'antd';
import { SyncOutlined } from '@ant-design/icons';

import { Query } from '../api/Query';
import { AuthorName } from '../api/AuthorRecommendation';
import { TryExamples } from '../components/shared';
import { FocalAuthorDisplay } from '../components/FocalAuthorDisplay';
import { QueryResult } from '../api/QueryResult';
import { AuthorAdditionalInfo, getAuthorInfo } from '../api/Author';

const { Link } = Typography;

type FormValue = Query;

const examples = [
    {
        label: 'Yejin Choi',
        queryString: new Query('1699545').toQueryString(),
    },
    {
        label: 'Eric Horvitz',
        queryString: new Query('145479841').toQueryString(),
    },
    {
        label: 'Hannaneh Hajishirzi',
        queryString: new Query('2548384').toQueryString(),
    },
    {
        label: 'Ilya Sutskever',
        queryString: new Query('1701686').toQueryString(),
    },
    {
        label: 'Jure Leskovec',
        queryString: new Query('1702139').toQueryString(),
    },
    {
        label: 'Marti A. Hearst',
        queryString: new Query('1716902').toQueryString(),
    },
    {
        label: 'Marinka Zitnik',
        queryString: new Query('2095762').toQueryString(),
    },
    {
        label: 'Aniket Kittur',
        queryString: new Query('145234497').toQueryString(),
    },
];

export const Home = () => {
    const history = useHistory();
    const location = useLocation();
    const [form] = Form.useForm<FormValue>();
    const [authorNames, setAuthorNames] = useState<AuthorName[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [authorInfo, setAuthorInfo] = useState<AuthorAdditionalInfo>();

    // on load, populate the search box with the user of the id in the url
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const id = queryParams.get('a');
        const year = queryParams.get('year') || '2017'; // TODO: don't hard code year
        if (id) {
            getAuthorInfo(id, year).then((response) => {
                setAuthorInfo(response);
            });
        }
    }, [location]);

    const handleSubmit = (focalAuthor: number) => {
        // We add the query params to the URL, so that users can link to
        // our demo and share noteworthy cases, edge cases, etc.
        const query = new Query(focalAuthor.toString());
        // pushing this new URL will automatically trigger a new query (see the 'useEffect' function above)
        history.push(`/?${query.toQueryString()}`);
        // TODO: need to keep year query param intact when changing authors
    };

    function searchForUsers(searchText: string) {
        const params = { q: searchText };
        const searchParams = new URLSearchParams(params);
        setLoading(true);
        axios
            .get<QueryResult<AuthorName[]>>(`/api/get_author_names?${searchParams}`)
            .then((response) => response.data)
            .then((response) => {
                if (JSON.stringify(params) === JSON.stringify(response.args)) {
                    setAuthorNames(response.data);
                    setLoading(false);
                }
            });
    }

    const handleInput = debounce(searchForUsers, 333);

    return (
        <React.Fragment>
            <p>
                Bridger is an author discovery system that helps you discover new authors and ideas. Explore authors who work on
                similar methods or problems.
            </p>
            <p>Instructions: <b>1.</b> Select an author. <b>2.</b> Select author subarea (if shown). <b>3.</b> Explore related authors.</p>
            <FormWrapper layout="horizontal" form={form} scrollToFirstError>
                <Form.Item label="Author:" rules={[{ required: true }]}>
                    <Select
                        value={
                            authorInfo
                                ? { label: authorInfo.name, value: authorInfo.authorId }
                                : undefined
                        }
                        suffixIcon={loading ? <SyncOutlined spin /> : null}
                        filterOption={false}
                        showSearch
                        notFoundContent={
                            loading ? (
                                <div>Loading…</div>
                            ) : (
                                <div>No authors found (type 3 or more characters)</div>
                            )
                        }
                        placeholder="Type 3 or more characters. If no match, try initials (E. Horvitz)."
                        optionLabelProp="label"
                        options={authorNames.map((name: AuthorName) => {
                            const affils = name.affiliations?.length
                                ? name.affiliations.join(', ')
                                : '';
                            const label = () => {
                                return (
                                    <div key={name.id}>
                                        <span>{name.name}</span>{' '}
                                        {affils ? <span>| {affils}</span> : null}{' '}
                                        <span>| {name.paperCount} papers</span>
                                    </div>
                                );
                            };
                            return { label: label(), value: name.id };
                        })}
                        onSearch={handleInput}
                        onSelect={(v: any) => {
                            handleSubmit(v);
                        }}></Select>
                    <TryExamples examples={examples} queryUrl={'/?'} />
                </Form.Item>
            </FormWrapper>
            <FocalAuthorDisplay />
        </React.Fragment>
    );
};

const FormWrapper = styled(Form)`
    max-width: 600px;
    margin-bottom: 24px;
`;
